// Level fragments: a run's board assembled from `.tmj` pieces.
//
// **A fragment is a level file holding ground and what stands on it** — the
// same rooms, tiles and markers as any level, drawn and round-tripped in the
// same editor, under `public/levels/fragments/`. A composed level is a list of
// fragments, each moved by an offset, and that list is a run kind's row
// (`RUN_CONFIG.fragments`), so Normal Mode's opener and its first camp are
// two files an author edits rather than one generated board.
//
// **Moved by its origin, never by its squares.** `placeFragment` adds the
// offset to the map's own `originFile`/`originRank` before the one real parser
// reads it, so every square-bearing field lands right without being listed
// here. Rooms are renumbered in placement order, since each file numbers its
// own from 1.
//
// **A fragment holds no objectives and no knobs.** Both are about a scenario,
// and a composite has no author to say which fragment's goal comes first.

import type { LevelSetup } from "src/engine/rules/campaign/level";
import type { Side } from "src/engine/rules/board";
import { LevelParseError, type TmjMap, type TmjProperty } from "./tmj";

/** One fragment of a composed level: its file under `levels/fragments/`, and how far it moves. */
export interface FragmentPlacement {
  fragment: string;
  /** Added to the fragment's own origin. */
  file: number;
  rank: number;
}

/** What a composed level says about itself — the map properties a fragment's own are ignored for. */
export interface ComposedLevel {
  levelId: string;
  levelName: string;
  playerSide: Side;
  fragments: readonly FragmentPlacement[];
}

/** Where a fragment's file sits, relative to `public/levels/`. */
export const fragmentPath = (fragment: string): string => `fragments/${fragment}.tmj`;

const offset = (properties: TmjProperty[] | undefined, name: string, by: number): TmjProperty => {
  const own = properties?.find((entry) => entry.name === name)?.value;
  return { name, type: "int", value: (typeof own === "number" ? own : 0) + by };
};

/** The fragment's map with its origin moved by the placement. */
export function placeFragment(map: TmjMap, at: FragmentPlacement): TmjMap {
  const rest = (map.properties ?? []).filter((entry) => entry.name !== "originFile" && entry.name !== "originRank");
  return {
    ...map,
    properties: [
      ...rest,
      offset(map.properties, "originFile", at.file),
      offset(map.properties, "originRank", at.rank)
    ]
  };
}

/** Join parsed, placed fragments into one level. Rooms renumber in order. */
export function composeLevel(head: ComposedLevel, parts: readonly LevelSetup[]): LevelSetup {
  const regions: LevelSetup["regions"] = [];
  parts.forEach((part, index) => {
    const name = head.fragments[index]?.fragment ?? `#${index}`;
    if (part.objectives.length > 0) {
      throw new LevelParseError(`fragment ${name} has objectives — a fragment is ground, not a scenario`);
    }
    if (part.knobs) throw new LevelParseError(`fragment ${name} has knobs — a fragment is ground, not a scenario`);
    for (const region of part.regions) regions.push({ ...region, id: String(regions.length + 1) });
  });
  return {
    levelId: head.levelId,
    levelName: head.levelName,
    playerSide: head.playerSide,
    regions,
    objectives: []
  };
}
